import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGestaoUsuarios } from '../context/GestaoUsuariosProvider';
import * as XLSX from 'xlsx';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  UserX, 
  UserCheck,
  Shield,
  Settings,
  EyeIcon,
  Download
} from 'lucide-react';

const ListaUsuarios = () => {
  const { 
    usuarios, 
    loading, 
    filtros, 
    setFiltros, 
    carregarUsuarios, 
    toggleStatusUsuario 
  } = useGestaoUsuarios();

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  // Aplicar filtros
  const usuariosFiltrados = usuarios.filter(usuario => {
    // Filtro por status
    if (filtros.status === 'ativo' && !usuario.ativo) return false;
    if (filtros.status === 'inativo' && usuario.ativo) return false;
    
    // Filtro por nível de acesso
    if (filtros.nivel_acesso && usuario.nivel_acesso !== filtros.nivel_acesso) return false;
    
    // Filtro por busca
    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      return (
        usuario.nome.toLowerCase().includes(busca) ||
        usuario.email?.toLowerCase().includes(busca) ||
        usuario.celular?.includes(busca)
      );
    }
    
    return true;
  });

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      status: '',
      nivel_acesso: '',
      busca: ''
    });
  };

  const handleToggleStatus = async (id, ativo) => {
    if (window.confirm(`Tem certeza que deseja ${ativo ? 'ativar' : 'desativar'} este usuário?`)) {
      await toggleStatusUsuario(id, ativo);
    }
  };

  const exportarParaExcel = () => {
    // Preparar dados para exportação
    const dadosExportacao = usuariosFiltrados.map(usuario => {
      const modulosAcesso = usuario.permissoes 
        ? Object.entries(usuario.permissoes)
            .filter(([key, value]) => value === true)
            .map(([modulo]) => getModuloLabel(modulo))
            .join(', ')
        : '';

      return {
        'Nome': usuario.nome,
        'Email': usuario.email || '',
        'Telefone': usuario.celular || '',
        'Status': usuario.ativo ? 'Ativo' : 'Inativo',
        'Nível de Acesso': getNivelAcessoLabel(usuario.nivel_acesso),
        'Módulos de Acesso': modulosAcesso,
        'Data de Cadastro': new Date(usuario.created_at).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      };
    });

    // Criar workbook e worksheet
    const ws = XLSX.utils.json_to_sheet(dadosExportacao);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuários');

    // Download do arquivo
    const dataAtual = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `usuarios_${dataAtual}.xlsx`);
  };

  const getNivelAcessoIcon = (nivel) => {
    switch (nivel) {
      case 'admin':
        return <Shield size={16} color="#dc3545" />;
      case 'operador':
        return <Settings size={16} color="#ffc107" />;
      case 'visualizador':
        return <EyeIcon size={16} color="#28a745" />;
      default:
        return <Users size={16} color="#6c757d" />;
    }
  };

  const getNivelAcessoLabel = (nivel) => {
    switch (nivel) {
      case 'admin':
        return 'Administrador';
      case 'operador':
        return 'Operador';
      case 'visualizador':
        return 'Visualizador';
      default:
        return 'Indefinido';
    }
  };

  const getModuloLabel = (modulo) => {
    const labels = {
      'obras': 'Obras',
      'obras-fixas': 'Obras Fixas',
      'fossas-septicas': 'Fossas Sépticas',
      'postes-iluminacao': 'Iluminação',
      'mapa-integrado': 'Mapa',
      'gestao-usuarios': 'Usuários'
    };
    return labels[modulo] || modulo;
  };

  if (loading) {
    return (
      <div className="gestao-usuarios lista-full-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios lista-full-container">
      <div className="lista-header">
        <div className="header-content">
          <h1>
            <Users size={32} />
            Gestão de Usuários
          </h1>
          <p>Gerencie usuários, permissões e acessos do sistema</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={exportarParaExcel}
            className="button secondary"
            disabled={usuariosFiltrados.length === 0}
          >
            <Download size={16} />
            Exportar Excel
          </button>
          <Link to="/gestao-usuarios/cadastro" className="button primary">
            <Plus size={16} />
            Novo Usuário
          </Link>
        </div>
      </div>

      <div className="lista-content-wrapper">
        {/* Barra de busca e filtros */}
        <div className="search-filter-bar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, email ou telefone..."
              value={filtros.busca}
              onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
            />
          </div>
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
          </button>
        </div>

        {/* Painel de filtros */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <label>Status</label>
                <select name="status" value={filtros.status} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  <option value="ativo">Ativos</option>
                  <option value="inativo">Inativos</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Nível de Acesso</label>
                <select name="nivel_acesso" value={filtros.nivel_acesso} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  <option value="admin">Administrador</option>
                  <option value="operador">Operador</option>
                  <option value="visualizador">Visualizador</option>
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button className="button secondary" onClick={limparFiltros}>
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Estatísticas rápidas */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{usuariosFiltrados.length}</span>
          </div>
          <div className="stat-item ativo">
            <span className="stat-label">Ativos:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => u.ativo).length}</span>
          </div>
          <div className="stat-item inativo">
            <span className="stat-label">Inativos:</span>
            <span className="stat-value">{usuariosFiltrados.filter(u => !u.ativo).length}</span>
          </div>
        </div>

        {/* Lista de usuários */}
        {usuariosFiltrados.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <h3>Nenhum usuário encontrado</h3>
            <p>Não há usuários que correspondam aos filtros aplicados.</p>
            <Link to="/gestao-usuarios/cadastro" className="button primary">
              <Plus size={16} />
              Cadastrar Primeiro Usuário
            </Link>
          </div>
        ) : (
          <div className="usuarios-list-vertical">
            {usuariosFiltrados.map(usuario => (
              <div key={usuario.id} className="usuario-card-horizontal">
                <div className="card-main-content">
                  <div className="card-header-section">
                    <div className="usuario-info">
                      <h3 className="usuario-title">{usuario.nome}</h3>
                      <div className="usuario-meta">
                        <span className="email">{usuario.email}</span>
                        {usuario.celular && (
                          <span className="telefone"> • {usuario.celular}</span>
                        )}
                      </div>
                    </div>
                    <div className="status-badges">
                      <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      <span className={`nivel-badge nivel-${usuario.nivel_acesso}`}>
                        {getNivelAcessoIcon(usuario.nivel_acesso)}
                        {getNivelAcessoLabel(usuario.nivel_acesso)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body-section">
                    <div className="info-line">
                      <span className="info-label">Cadastrado:</span>
                      <span className="info-value data-criacao">
                        {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    {usuario.permissoes && Object.keys(usuario.permissoes).length > 0 && (
                      <div className="info-line">
                        <span className="info-label">Módulos de acesso:</span>
                        <div className="info-value">
                          <div className="modulos-tags">
                            {Object.entries(usuario.permissoes)
                              .filter(([key, value]) => value === true)
                              .map(([modulo]) => (
                                <span key={modulo} className="modulo-tag">
                                  {getModuloLabel(modulo)}
                                </span>
                              ))
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-actions-vertical">
                  <Link 
                    to={`/gestao-usuarios/detalhes/${usuario.id}`}
                    className="action-button-full view"
                  >
                    <Eye size={16} />
                    Ver Detalhes
                  </Link>
                  
                  <Link 
                    to={`/gestao-usuarios/editar/${usuario.id}`}
                    className="action-button-full edit"
                  >
                    <Edit size={16} />
                    Editar
                  </Link>
                  
                  <button
                    onClick={() => handleToggleStatus(usuario.id, !usuario.ativo)}
                    className={`action-button-full ${usuario.ativo ? 'deactivate' : 'activate'}`}
                  >
                    {usuario.ativo ? (
                      <>
                        <UserX size={16} />
                        Desativar
                      </>
                    ) : (
                      <>
                        <UserCheck size={16} />
                        Ativar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaUsuarios;